/**
 * Business defaults sub-region inspect view per scope §6.2. Atlas Day 2.5
 * P6 follows PerDistributorInspect canonical pattern with subsection-
 * altitude L5/L6/L9 (not per-field) per Day 86 A6 PARAM_GROUPS_SETTINGS
 * structure surfaced in classificationMaps.params.
 *
 * No clusterName — Business defaults is a flat region at (substrate,
 * calculation) cell, not within a cluster per scope §4.1 cluster-principle
 * three-mode framing (flat-regions mode).
 *
 * PARAM_GROUPS_SETTINGS cross-app import per Atlas Day 2 P0 additional
 * finding ruling is a Day 88+ refinement candidate; current inspect reads
 * subsection-altitude classifications from FIELD_CLASSIFICATIONS.params
 * (7 subsections: engine_tuning, fulfillment_costs, margins, returns,
 * credit_thresholds, marketplace_fees, timing).
 */

import type { JSX } from 'react';
import { FIELD_CLASSIFICATIONS } from '../../../lib/atlas/classificationMaps';
import { BUSINESS_DEFAULTS_PROPAGATION } from '../../../lib/atlas/propagationMaps';
import InspectShell from './InspectShell';
import LayerSection from './LayerSection';
import ComposabilityReservedLayer from './ComposabilityReservedLayer';

export default function BusinessDefaultsInspect(): JSX.Element {
  const paramsSubsections = FIELD_CLASSIFICATIONS.params;
  const subsectionEntries = Object.entries(paramsSubsections);

  const ownershipSubsections = subsectionEntries.filter(
    ([, c]) => c.layer6Authority === 'ownership'
  );
  const operationalSubsections = subsectionEntries.filter(
    ([, c]) => c.layer6Authority === 'operational'
  );
  const naLayer6Subsections = subsectionEntries.filter(([, c]) => c.layer6Authority === undefined);

  return (
    <InspectShell
      subRegionName="Business defaults"
      surfaceLine="Universal engine inputs ISG hasn't varied per entity. ← overridden by per-entity rows"
    >
      <LayerSection layer={4} title="Math vs judgment">
        <p>
          Per scope §6.2: section-by-section split. Most business-level params ={' '}
          <strong>judgment</strong> (min_roi_pct, returns_reserve_pct, urgency thresholds, Buy
          Score weights — ISG&apos;s strategic-position choices). Few = <strong>mechanical</strong>{' '}
          (estimated_tax pulls regional rates; marketplace fees pull marketplace-set rates).
          cost_line_items base rows = <strong>judgment</strong> (ISG&apos;s choices about
          cost-component definitions).
        </p>
      </LayerSection>

      <LayerSection layer={5} title="Mechanical vs strategic">
        <p>
          Subsection-by-subsection split per Day 86 A6 PARAM_GROUPS_SETTINGS structure (scope
          §6.2 inspect-content organization sub-corollary):
        </p>
        <ul className="list-disc pl-5 text-xs space-y-1">
          {subsectionEntries.map(([subsection, c]) => (
            <li key={subsection}>
              <strong>
                <code>{subsection}</code>
              </strong>{' '}
              — <span className="font-mono">{c.layer5Nature}</span>
              {c.rationale ? ` (${c.rationale.split(';')[0]})` : ''}
            </li>
          ))}
        </ul>
      </LayerSection>

      <LayerSection layer={6} title="Operational vs ownership">
        <div>
          <h3 className="text-xs font-semibold text-ink-bright mb-1">
            Ownership-altitude subsections ({ownershipSubsections.length}) — change-with-care
            (per Day 86 A6 Engine Tuning framing)
          </h3>
          <ul className="list-disc pl-5 text-xs space-y-0.5">
            {ownershipSubsections.map(([subsection]) => (
              <li key={subsection}>
                <code>{subsection}</code>
              </li>
            ))}
          </ul>
        </div>
        <div className="mt-3">
          <h3 className="text-xs font-semibold text-ink-bright mb-1">
            Operational-altitude subsections ({operationalSubsections.length}) — routine-tunable
          </h3>
          <ul className="list-disc pl-5 text-xs space-y-0.5">
            {operationalSubsections.map(([subsection]) => (
              <li key={subsection}>
                <code>{subsection}</code>
              </li>
            ))}
          </ul>
        </div>
        {naLayer6Subsections.length > 0 && (
          <div className="mt-3">
            <h3 className="text-xs font-semibold text-ink-bright mb-1">
              Region-altitude subsections ({naLayer6Subsections.length}, layer 6 N/A per
              three-state applicability) — external facts
            </h3>
            <ul className="list-disc pl-5 text-xs space-y-0.5">
              {naLayer6Subsections.map(([subsection, c]) => (
                <li key={subsection}>
                  <code>{subsection}</code>
                  {c.rationale ? ` — ${c.rationale.split(';')[0]}` : ''}
                </li>
              ))}
            </ul>
          </div>
        )}
      </LayerSection>

      <LayerSection layer={7} title="Trace">
        <p>
          Per scope §6.2 L7 + Atlas Day 2 Phase 0 #7: <code>params.provenance</code> column
          exists (Day 67 P2.2 substrate). All 33 business-level rows currently{' '}
          <strong>backfill-traced</strong> (provenance populated but no Day-82-style{' '}
          <code>provenance.at</code> timestamp yet). Per-row trace shows source + set_by +
          changed_reason but not precise change timestamps.
        </p>
        <p>
          <code>cost_line_items.provenance</code> sidecar exists (Day 67 P2.2). Same
          three-pattern framing per row.
        </p>
      </LayerSection>

      <LayerSection layer={8} title="Propagation">
        <p>
          Hybrid (c) categorical default for most params + (a) curated maps for high-stakes
          ownership-altitude values per scope §6.2 L8. Curated map (verified as of 2026-05-27
          per scope §8 staleness marker):
        </p>
        <ul className="list-disc pl-5 text-xs space-y-2">
          {BUSINESS_DEFAULTS_PROPAGATION.map((entry) => (
            <li key={entry.field}>
              <strong>
                <code>{entry.field}</code>
              </strong>{' '}
              edits affect:
              <ul className="list-circle pl-5 mt-1 space-y-0.5">
                {entry.affects.map((eff) => (
                  <li key={eff}>{eff}</li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </LayerSection>

      <LayerSection layer={9} title="Reversibility">
        <p>
          Per scope §6.2 L9: mostly reversible-with-residue. Per-param edits propagate to
          future calculations cleanly. Past <code>product_profitability</code> cache rows
          hold prior-value calculations; <code>buying_sessions.param_snapshot</code> JSONB
          locks scenarios to historical param state. Per-subsection:
        </p>
        <ul className="list-disc pl-5 text-xs space-y-0.5">
          {subsectionEntries.map(([subsection, c]) => (
            <li key={subsection}>
              <code>{subsection}</code> —{' '}
              <span className="font-mono">{c.layer9Reversibility}</span>
            </li>
          ))}
        </ul>
      </LayerSection>

      <ComposabilityReservedLayer />

      <LayerSection layer={11} title="Positional contribution">
        <p>
          Substantive-contribution per scope §6.2 L11. Business defaults contribute to
          ISG&apos;s position by encoding the universal engine inputs ISG hasn&apos;t varied
          per entity. <code>min_roi_pct</code> surfaces in title block sentence 1 ROI clause;{' '}
          <code>returns_reserve_pct</code> surfaces as returns-buffer stance applied
          universally; <code>days_to_sell_default</code> surfaces in title block sentence 1
          holding-period clause (per Day 1 D1 substrate source).
        </p>
        <p>
          Bidirectional override pointer: ← overridden by per-entity rows (per-entity cluster
          sub-regions can override these defaults for specific distributor / supplier / brand /
          category / product entities per param hierarchy).
        </p>
      </LayerSection>
    </InspectShell>
  );
}
